import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { theme, FONT_RUNE } from "./theme";
import { drawPiece } from "./pieces";
import { eased, progress } from "./animations";
import { useGame } from "./store";
import { FILES, RANKS, fromKey, squareKey, type GameState, type Piece, type Square } from "../engine";

export function BoardCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cellRef = useRef<number>(40);
  const offsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // Ref to the latest drawAll so the mounted-once rAF loop always calls the freshest closure.
  const drawAllRef = useRef<() => void>(() => {});

  const game = useGame((s) => s.game);
  const selected = useGame((s) => s.selected);
  const destinations = useGame((s) => s.destinations);
  const pending = useGame((s) => s.pending);
  const animations = useGame((s) => s.animations);
  const selectSquare = useGame((s) => s.selectSquare);
  // Board orientation: the viewer's color sits at the bottom of the screen.
  // Bot mode → humanSide. Online → my side. Local hotseat / tutorial → "black" (V1's
  // natural orientation — Black at bottom because Black moves first).
  const viewSide = useGame((s) => {
    if (s.mode === "bot") return s.humanSide;
    if (s.online) return s.online.mySide;
    return "black" as const;
  });
  const flipRank = viewSide === "white";
  const screenRank = (engineRank: number) => (flipRank ? RANKS - 1 - engineRank : engineRank);

  // Snapshot state mid-animation: animations refer to "from→to" which has already been applied to game.board.
  // So during a slide, the piece is at `to` in state but visually we draw it interpolated.

  // Resize observer
  useLayoutEffect(() => {
    if (!containerRef.current || !canvasRef.current) return;
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const cssW = rect.width;
      const cssH = rect.height;
      // Reserve room on the top and left for file/rank labels.
      const LABEL_MARGIN = 16;
      const usableW = Math.max(0, cssW - LABEL_MARGIN);
      const usableH = Math.max(0, cssH - LABEL_MARGIN);
      const cellW = usableW / FILES;
      const cellH = usableH / RANKS;
      const cell = Math.floor(Math.min(cellW, cellH));
      cellRef.current = cell;
      const boardW = cell * FILES;
      const boardH = cell * RANKS;
      offsetRef.current = {
        x: LABEL_MARGIN + Math.floor((usableW - boardW) / 2),
        y: LABEL_MARGIN + Math.floor((usableH - boardH) / 2),
      };
      canvas.width = Math.floor(cssW * dpr);
      canvas.height = Math.floor(cssH * dpr);
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // Keep drawAllRef pointed at the freshest closure — drawAll captures all the React state
  // we render with, so we need to swap the ref on every render to read up-to-date values
  // from inside the long-lived rAF loop below.
  drawAllRef.current = drawAll;

  // Animation loop. Mounted once and never torn down. The previous implementation listed
  // game / selected / destinations / pending / animations as deps, so any state change
  // cancelled the rAF and queued a new one, leaving a ~1-frame gap that visibly flashed
  // mid-move. Now we mount once, call drawAllRef.current each frame, and read the store
  // directly for animation cleanup.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      drawAllRef.current();
      const now = performance.now();
      const live = useGame.getState().animations;
      for (const a of live) {
        if (progress(a, now) >= 1) useGame.getState().ackAnimation(a.id);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  function pointerToSquare(clientX: number, clientY: number): Square | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left - offsetRef.current.x;
    const y = clientY - rect.top - offsetRef.current.y;
    const cell = cellRef.current;
    const file = Math.floor(x / cell);
    const sRank = Math.floor(y / cell);
    if (file < 0 || file >= FILES || sRank < 0 || sRank >= RANKS) return null;
    // Inverse of screenRank: screenRank is involutive (its own inverse), so reuse it.
    return { file, rank: screenRank(sRank) };
  }

  function drawAll() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const cell = cellRef.current;
    const off = offsetRef.current;

    // Clear to transparent so the wood-table background shows through the canvas margins.
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Board background
    drawBoard(ctx, cell, off);

    // Highlights: selected square, legal destinations, capture targets
    if (selected) {
      drawSquareHighlight(ctx, selected, cell, off, theme.selected, 0.32);
    }
    for (const key of destinations.keys()) {
      drawDotHighlight(ctx, fromKey(key as `${number},${number}`), cell, off, theme.legalMove);
    }
    if (pending) {
      for (const c of pending.declarable) {
        drawSquareHighlight(ctx, c.target, cell, off, theme.captureTarget, 0.4);
      }
    }

    // Pieces — with slide interpolation
    const now = performance.now();
    const slideAnims = animations.filter((a) => a.kind === "slide" && a.from && a.to);
    const flashFor = (sq: Square) =>
      animations.find((a) => a.kind === "captureFlash" && a.square && sameSquare(a.square, sq));

    const sliding = new Set<string>();
    for (const s of slideAnims) {
      if (s.to) sliding.add(squareKey(s.to));
    }

    for (const [k, piece] of game.board) {
      const sq = fromKey(k);
      // Skip pieces that are currently sliding to here — drawn interpolated below.
      if (sliding.has(k)) continue;
      const center = squareCenter(sq, cell, off, flipRank);
      const fa = flashFor(sq);
      drawPiece(ctx, {
        cell,
        cx: center.x,
        cy: center.y,
        piece,
        selected: selected ? sameSquare(selected, sq) : false,
        capturePulse: fa ? Math.max(0, 1 - eased(fa, now)) : 0,
      });
    }

    // Draw each sliding piece interpolated from its `from` to its `to`.
    for (const slide of slideAnims) {
      if (!slide.from || !slide.to) continue;
      const piece = game.board.get(squareKey(slide.to));
      if (!piece) continue;
      const t = eased(slide, now);
      const a = squareCenter(slide.from, cell, off, flipRank);
      const b = squareCenter(slide.to, cell, off, flipRank);
      const cx = a.x + (b.x - a.x) * t;
      const cy = a.y + (b.y - a.y) * t;
      drawPiece(ctx, { cell, cx, cy, piece });
    }

    // Capture-only flashes (target already removed from board): show ring at the spot
    for (const a of animations) {
      if (a.kind !== "captureFlash" || !a.square) continue;
      if (game.board.has(squareKey(a.square))) continue;
      const t = eased(a, now);
      const alpha = Math.max(0, 1 - t);
      const center = squareCenter(a.square, cell, off, flipRank);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = theme.captureFlash;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(center.x, center.y, cell * (0.45 + 0.25 * t), 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Floating math labels above just-captured squares ("4 + 5 = 9", "12 = 12", etc).
    for (const a of animations) {
      if (a.kind !== "captureLabel" || !a.square || !a.text) continue;
      const t = eased(a, now);
      // Fade-in over first 10%, hold ~70%, fade out last 20%
      const alpha =
        t < 0.1 ? t / 0.1 : t < 0.8 ? 1 : Math.max(0, (1 - t) / 0.2);
      const center = squareCenter(a.square, cell, off, flipRank);
      // Rise as it fades
      const yLift = cell * 0.55 + t * cell * 0.5;
      const fontSize = Math.max(13, Math.min(28, cell * 0.34));
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.font = `600 ${fontSize}px ${FONT_RUNE}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      // Outline for contrast over any board color
      ctx.strokeStyle = theme.bg;
      ctx.lineWidth = Math.max(3, fontSize / 6);
      ctx.lineJoin = "round";
      ctx.strokeText(a.text, center.x, center.y - yLift);
      ctx.fillStyle = theme.accent;
      ctx.fillText(a.text, center.x, center.y - yLift);
      ctx.restore();
    }
  }

  // Board background, alternating squares, file/rank labels
  function drawBoard(ctx: CanvasRenderingContext2D, cell: number, off: { x: number; y: number }) {
    // Soft drop shadow underneath the board: fill an opaque rect with shadow style set, then
    // overpaint with the squares. Only the shadow's outward bleed is visible, giving the board
    // a slight "sitting above the wood" lift.
    ctx.save();
    ctx.fillStyle = theme.boardDark;
    ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
    ctx.shadowBlur = Math.max(10, cell * 0.32);
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = Math.max(3, cell * 0.08);
    ctx.fillRect(off.x, off.y, cell * FILES, cell * RANKS);
    ctx.restore();

    for (let sr = 0; sr < RANKS; sr++) {
      for (let file = 0; file < FILES; file++) {
        // Color parity is purely cosmetic — base it on screen position so the checkerboard
        // pattern looks identical regardless of orientation.
        const light = (file + sr) % 2 === 0;
        ctx.fillStyle = light ? theme.boardLight : theme.boardDark;
        ctx.fillRect(off.x + file * cell, off.y + sr * cell, cell, cell);
      }
    }
    // Border
    ctx.strokeStyle = theme.boardEdge;
    ctx.lineWidth = 2;
    ctx.strokeRect(off.x, off.y, cell * FILES, cell * RANKS);

    // File labels (A..H) along top and rank labels (1..16) along the left.
    ctx.fillStyle = theme.boardLabel;
    const labelSize = Math.max(9, Math.min(13, cell * 0.22));
    ctx.font = `${labelSize}px ${FONT_RUNE}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    for (let f = 0; f < FILES; f++) {
      ctx.fillText("ABCDEFGH"[f], off.x + (f + 0.5) * cell, off.y - 4);
    }
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let sr = 0; sr < RANKS; sr++) {
      // Screen row sr displays engine rank screenRank(sr); algebraic label is that +1.
      ctx.fillText(String(screenRank(sr) + 1), off.x - 4, off.y + (sr + 0.5) * cell);
    }
  }

  function drawSquareHighlight(ctx: CanvasRenderingContext2D, sq: Square, cell: number, off: { x: number; y: number }, color: string, alpha: number) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.fillRect(off.x + sq.file * cell, off.y + screenRank(sq.rank) * cell, cell, cell);
    ctx.restore();
  }

  function drawDotHighlight(ctx: CanvasRenderingContext2D, sq: Square, cell: number, off: { x: number; y: number }, color: string) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.55;
    ctx.beginPath();
    ctx.arc(off.x + (sq.file + 0.5) * cell, off.y + (screenRank(sq.rank) + 0.5) * cell, cell * 0.16, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Memoize a stable click handler. flipRank gates the screen→engine rank inversion inside
  // pointerToSquare, so changes in board orientation must rebuild the handler.
  const onPointerDown = useMemo(
    () => (ev: React.PointerEvent<HTMLCanvasElement>) => {
      const sq = pointerToSquare(ev.clientX, ev.clientY);
      selectSquare(sq);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectSquare, flipRank],
  );

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        flex: "0 1 auto",
        minHeight: 0,
        height: "100%",
        width: "100%",
        // Board aspect ratio is FILES:RANKS (1:2), plus 16px reserved on top/left for labels.
        // 50cqh = half the height of the containing size container = ideal board width.
        maxWidth: "calc(50cqh + 16px)",
        touchAction: "manipulation",
      }}
    >
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        style={{ display: "block", width: "100%", height: "100%" }}
      />
    </div>
  );
}

function squareCenter(sq: Square, cell: number, off: { x: number; y: number }, flipRank: boolean) {
  const sr = flipRank ? RANKS - 1 - sq.rank : sq.rank;
  return { x: off.x + (sq.file + 0.5) * cell, y: off.y + (sr + 0.5) * cell };
}

function sameSquare(a: Square, b: Square): boolean {
  return a.file === b.file && a.rank === b.rank;
}

// Helper used only for type inference
export type { GameState, Piece };
